/**
 * 申請回数一覧 - Rezept Application Count List Page
 * UI pattern: same as 文章テンプレート一覧 (document_templates/init).
 * - Table with 申請回数一覧 card, 新規登録 opens modal, 編集/削除 per row.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppSpin } from '@/components/atoms';
import PatientPageHeader from '@pages/main/patient/components/PatientPageHeader';
import { AppButton } from '@/components/atoms';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { useRezeptApplicationCountList } from '@/hooks/main/useRezeptApplicationCountList';
import {
  useRezeptApplicationCountCreate,
  useRezeptApplicationCountUpdate,
  useRezeptApplicationCountDelete,
} from '@/hooks/main/useRezeptApplicationCountEdit';
import RezeptApplicationCountListSection from '../components/RezeptApplicationCountListSection';
import RezeptApplicationCountEditModal from '../components/RezeptApplicationCountEditModal';
import {
  fetchRezeptApplicationCountOne,
  type RezeptApplicationCountItem,
} from '@/services/rezeptApplicationCountService';
import type { RezeptApplicationCountFormData } from '../components/RezeptApplicationCountEditModal';
import { getApiErrorMessage, isNotFoundError } from '@/utils/apiError';
import { NotFound } from '@/pages/main/system/NotFound';
import { DELETE_CONFIRM_DATA_MESSAGE } from '@/constants/messages';

export const RezeptApplicationCountListPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ tenant?: string; patientId?: string }>();
  const { tenant: siteType, clinicNumber } = useClinicContext();
  const tenant = params.tenant;
  const patientId = params.patientId;

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<RezeptApplicationCountItem | null>(null);
  const [isNewRecord, setIsNewRecord] = useState(false);

  const toast = useAppToast();
  const { confirm } = useAppModal();

  const derived = useMemo(
    () => ({
      siteType,
      clinicNumber,
      patientId: patientId || '',
    }),
    [siteType, clinicNumber, patientId]
  );

  const { data, isLoading, isFetching, isError, refetch } = useRezeptApplicationCountList(
    derived.patientId
  );
  const createMutation = useRezeptApplicationCountCreate(derived.patientId);
  const updateMutation = useRezeptApplicationCountUpdate(derived.patientId);
  const deleteMutation = useRezeptApplicationCountDelete(derived.patientId);

  const patient = data?.patient;
  const list = data?.list ?? [];

  const patientLabel = useMemo(() => {
    const serial = patient?.serial_number ?? '';
    const name = patient?.current_patient_information?.name ?? '';
    return `${serial}：${name}`;
  }, [patient]);

  const handleNewClick = () => {
    setEditingRecord(null);
    setIsNewRecord(true);
    setEditModalOpen(true);
  };

  const handleEditClick = async (record: RezeptApplicationCountItem) => {
    try {
      await fetchRezeptApplicationCountOne(
        derived.siteType,
        derived.clinicNumber,
        derived.patientId,
        String(record.id)
      );
      setEditingRecord(record);
      setIsNewRecord(false);
      setEditModalOpen(true);
    } catch (err) {
      if (isNotFoundError(err)) {
        toast.error('データが見つかりません。');
        void refetch();
        return;
      }
      toast.error(getApiErrorMessage(err, '更新ができませんでした。'));
    }
  };

  const handleDeleteClick = (record: RezeptApplicationCountItem) => {
    confirm({
      title: DELETE_CONFIRM_DATA_MESSAGE,
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(record.id);
        } catch (err) {
          toast.error(getApiErrorMessage(err, '削除ができませんでした。'));
          throw err;
        }
      },
    });
  };

  const handleSave = async (formData: RezeptApplicationCountFormData) => {
    if (isNewRecord) {
      await createMutation.mutateAsync(formData);
    } else if (editingRecord) {
      await updateMutation.mutateAsync({
        id: editingRecord.id,
        payload: formData,
      });
    }
    setEditModalOpen(false);
    setEditingRecord(null);
    setIsNewRecord(false);
  };

  const showLoading = isLoading || isFetching;

  if (isError && !data) {
    return <NotFound />;
  }

  return (
    <AppSpin spinning={showLoading}>
      <div className="pb-24">
        <RezeptApplicationCountListSection
          list={list}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          header={
            <PatientPageHeader
              label={patientLabel}
              patientId={derived.patientId}
              tenant={tenant}
              navigate={navigate}
              activeKey="application-counts"
              actions={
                <AppButton
                  type="primary"
                  onClick={handleNewClick}
                  icon={<i className="fa fa-file-text-o" />}
                >
                  新規作成
                </AppButton>
              }
            />
          }
        />

        <RezeptApplicationCountEditModal
          record={editingRecord}
          patientId={derived.patientId}
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setEditingRecord(null);
            setIsNewRecord(false);
          }}
          onSave={handleSave}
          onDelete={record => {
            setEditModalOpen(false);
            handleDeleteClick(record);
          }}
          isNew={isNewRecord}
        />
      </div>
    </AppSpin>
  );
};

export default RezeptApplicationCountListPage;
